/**
 * FilterBar Component
 * 
 * Faceted search with chip-based active filters and dropdown selectors.
 * Supports multiple facets with single or multi-select options.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { X, ChevronDown, Filter, Check } from 'lucide-react';
import type { FilterBarProps, FacetDefinition, ActiveFilter, FacetOption } from './types';

// ============================================================================
// Facet Dropdown Component
// ============================================================================

interface FacetDropdownProps<T = string> {
  facet: FacetDefinition<T>;
  activeFilters: ActiveFilter<T>[];
  onAddFilter: (filter: Omit<ActiveFilter<T>, 'id'>) => void;
  onRemoveFilter: (filterId: string) => void;
}

function FacetDropdown<T = string>({
  facet,
  activeFilters,
  onAddFilter,
  onRemoveFilter,
}: FacetDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Get selected values for this facet
  const selectedValues = activeFilters
    .filter(f => f.facetKey === facet.key)
    .map(f => f.value)
    .flat();
  
  // Filter options by search
  const filteredOptions = facet.searchable && searchTerm
    ? facet.options.filter(opt => 
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : facet.options;
  
  const handleOptionClick = (option: FacetOption) => {
    const isSelected = selectedValues.includes(option.value);
    
    if (isSelected) {
      // Remove filter
      const filter = activeFilters.find(
        f => f.facetKey === facet.key && 
        (f.value === option.value || (Array.isArray(f.value) && f.value.includes(option.value)))
      );
      if (filter) {
        onRemoveFilter(filter.id);
      }
    } else {
      // Add filter
      onAddFilter({
        facetKey: facet.key,
        operator: 'equals',
        value: option.value,
        label: `${facet.label}: ${option.label}`,
      });
    }
    
    if (!facet.multi) {
      setIsOpen(false);
    }
  };
  
  const hasActiveFilters = selectedValues.length > 0;
  const Icon = facet.icon || Filter;
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm  border
                   transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500
                   ${hasActiveFilters 
                     ? 'bg-primary-50 border-primary-300 text-primary-700 hover:bg-primary-100' 
                     : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
                   }`}
      >
        <Icon className="h-4 w-4" />
        <span>{facet.label}</span>
        {hasActiveFilters && (
          <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-primary-200 text-primary-800 rounded-full">
            {selectedValues.length}
          </span>
        )}
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-[var(--bg-raised)] shadow-lg border border-[var(--rule)] z-50 overflow-hidden">
          {/* Search input for searchable facets */}
          {facet.searchable && (
            <div className="p-2 border-b border-[var(--rule)]">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={`Search ${facet.label.toLowerCase()}...`}
                className="w-full px-3 py-1.5 text-sm border border-[var(--rule)] rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
            </div>
          )}
          
          {/* Options list */}
          <div className="max-h-60 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[var(--text-ter)] text-center">
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                const OptionIcon = option.icon;
                
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleOptionClick(option)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left
                               hover:bg-[var(--surface)] transition-colors
                               ${isSelected ? 'bg-primary-50 text-primary-700' : 'text-[var(--text-sec)]'}`}
                  >
                    {/* Checkbox for multi-select */}
                    {facet.multi && (
                      <div className={`w-4 h-4 rounded border flex items-center justify-center
                                      ${isSelected 
                                        ? 'bg-primary-600 border-primary-600' 
                                        : 'border-[var(--rule)]'}`}>
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </div>
                    )}
                    
                    {/* Option icon */}
                    {OptionIcon && <OptionIcon className="h-4 w-4 text-[var(--text-ter)]" />}
                    
                    {/* Color indicator */}
                    {option.color && (
                      <span 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: option.color }}
                      />
                    )}
                    
                    {/* Label and count */}
                    <span className="flex-1">{option.label}</span>
                    {option.count !== undefined && (
                      <span className="text-xs text-[var(--text-ter)]">{option.count}</span>
                    )}
                    
                    {/* Radio check for single select */}
                    {!facet.multi && isSelected && (
                      <Check className="h-4 w-4 text-primary-600" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Active Filter Chip Component
// ============================================================================

interface FilterChipProps {
  label: string;
  onRemove: () => void;
}

function FilterChip({ label, onRemove }: FilterChipProps) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 text-sm font-medium bg-primary-100 text-primary-800 rounded-full">
      <span>{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="p-0.5 rounded-full hover:bg-primary-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

// ============================================================================
// FilterBar Component
// ============================================================================

export function FilterBar<T = string>({
  facets,
  activeFilters,
  onFilterChange,
  className = '',
}: FilterBarProps<T>) {
  const handleAddFilter = useCallback((filter: Omit<ActiveFilter<T>, 'id'>) => {
    const newFilter: ActiveFilter<T> = {
      ...filter,
      id: `${String(filter.facetKey)}-${String(filter.value)}-${Date.now()}`,
    };
    onFilterChange([...activeFilters, newFilter]);
  }, [activeFilters, onFilterChange]);
  
  const handleRemoveFilter = useCallback((filterId: string) => {
    onFilterChange(activeFilters.filter(f => f.id !== filterId));
  }, [activeFilters, onFilterChange]);
  
  const handleClearAll = useCallback(() => {
    onFilterChange([]);
  }, [onFilterChange]);
  
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Facet dropdowns */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-[var(--text-ter)] flex items-center gap-1.5">
          <Filter className="h-4 w-4" />
          Filter by:
        </span>
        
        {facets.map((facet) => (
          <FacetDropdown
            key={String(facet.key)}
            facet={facet}
            activeFilters={activeFilters}
            onAddFilter={handleAddFilter}
            onRemoveFilter={handleRemoveFilter}
          />
        ))}
      </div>
      
      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {activeFilters.map((filter) => (
            <FilterChip
              key={filter.id}
              label={filter.label}
              onRemove={() => handleRemoveFilter(filter.id)}
            />
          ))}
          
          <button
            type="button"
            onClick={handleClearAll}
            className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] underline"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

export default FilterBar;
